import React, { useState } from 'react';
import { Container, Typography, CircularProgress, Alert } from '@mui/material';
import { useDashboard } from '../hooks/useDashboard';
import InquiryFilter from '../components/inquiryAnalytics/InquiryFilter';
import InquiryOverview from '../components/inquiryAnalytics/InquiryOverview';
import InquiryTrendChart from '../components/inquiryAnalytics/InquiryTrendChart';
import { useWarehouseContext } from '../context/WarehouseContext';
import { useTranslation } from 'react-i18next';

const InquiryAnalytics = () => {
    const { t } = useTranslation();
    const { getInquiriesSummary, getInquiriesTrend } = useDashboard();

    const [period, setPeriod] = useState('last_30_days');
    const [dateRange, setDateRange] = useState(() => {
        const today = new Date();
        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        return {
            from_date: startOfMonth.toISOString().split('T')[0],
            to_date: endOfMonth.toISOString().split('T')[0],
        };
    });

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { selectedWarehouseIds } = useWarehouseContext();

    const [data, setData] = useState({ summary: null, trend: null });

    const fetchingRef = React.useRef(false);

    const fetchData = React.useCallback(async (force = false) => {
        if (period === 'custom' && (!dateRange.from_date || !dateRange.to_date)) return;
        if (fetchingRef.current) return;

        const warehouseKey = selectedWarehouseIds?.length > 0 ? `_w${selectedWarehouseIds.join('_')}` : '';
        const cacheKey = period === 'custom'
            ? `inquiryAnalytics_custom_${dateRange.from_date}_${dateRange.to_date}${warehouseKey}`
            : `inquiryAnalytics_${period}${warehouseKey}`;
        const cachedData = localStorage.getItem(cacheKey);

        if (!force && cachedData) {
            try {
                const parsed = JSON.parse(cachedData);
                if (parsed) { setData(parsed); return; }
            } catch (e) {
                localStorage.removeItem(cacheKey);
            }
        }

        fetchingRef.current = true;
        setLoading(true);
        setError(null);

        try {
            const params = { period };
            if (period === 'custom') {
                params.from_date = dateRange.from_date;
                params.to_date = dateRange.to_date;
            }

            const [summaryRes, trendRes] = await Promise.all([
                getInquiriesSummary(params),
                getInquiriesTrend(params)
            ]);

            const newError = summaryRes.error || trendRes.error;
            if (newError) setError(newError);

            const newData = {
                summary: summaryRes.data || null,
                trend: trendRes.data || null
            };

            setData(newData);
            localStorage.setItem(cacheKey, JSON.stringify(newData));
        } catch (err) {
            setError(err.message || t('hookErrors.analyticsPage.fetchFailed'));
        } finally {
            setLoading(false);
            fetchingRef.current = false;
        }
    }, [period, dateRange, getInquiriesSummary, getInquiriesTrend, selectedWarehouseIds]);

    React.useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedWarehouseIds]);

    const strings = t('inquiryAnalytics.page', { returnObjects: true });

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            {/* Header */}
            <div style={{ marginBottom: 36 }}>
                <Typography
                    variant="h3"
                    component="h1"
                    fontWeight="800"
                    sx={{
                        background: 'linear-gradient(45deg, #05445E 30%, #189AB4 90%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        mb: 1
                    }}
                >
                    {strings.title}
                </Typography>
                <Typography variant="subtitle1" color="textSecondary" sx={{ fontWeight: 500 }}>
                    {strings.subtitle}
                </Typography>
            </div>

            <InquiryFilter
                fromDate={dateRange.from_date}
                toDate={dateRange.to_date}
                onFromDateChange={(val) => setDateRange(prev => ({ ...prev, from_date: val }))}
                onToDateChange={(val) => setDateRange(prev => ({ ...prev, to_date: val }))}
                onSubmit={() => fetchData(true)}
                loading={loading}
                period={period}
                onPeriodChange={setPeriod}
            />

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {typeof error === 'object' ? JSON.stringify(error) : error}
                </Alert>
            )}

            {loading && !data.summary && (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
                    <CircularProgress />
                </div>
            )}

            {data.summary && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
                    <InquiryOverview data={data.summary} />
                    {data.trend && <InquiryTrendChart data={data.trend} />}
                </div>
            )}
        </Container>
    );
};

export default InquiryAnalytics;
